"use client";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";

const brandColors = ["#71D9C5", "#FFE28A", "#F6BBCD"]; // teal, yellow, pink
const gridSize = 12; // Smaller grid for subtlety

function hexToRgba(hex: string, alpha = 1) {
  const [r, g, b] = hex.match(/\w\w/g)!.map((x) => parseInt(x, 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default function BookPixelBackground() {
  const [grid, setGrid] = useState<string[][]>([]);

  useEffect(() => {
    // Initialize grid with vibrant colors
    const newGrid = Array.from({ length: gridSize }, () =>
      Array.from(
        { length: gridSize },
        () => brandColors[Math.floor(Math.random() * brandColors.length)],
      ),
    );
    setGrid(newGrid);

    // Update pixels more frequently and change more at once
    const interval = setInterval(() => {
      setGrid((currentGrid) => {
        if (currentGrid.length !== gridSize) return currentGrid;

        const updatedGrid = currentGrid.map((row) => [...row]);
        // Change more pixels at a time for more animation
        const numToChange = Math.floor(Math.random() * 8) + 5;

        for (let i = 0; i < numToChange; i++) {
          const x = Math.floor(Math.random() * gridSize);
          const y = Math.floor(Math.random() * gridSize);
          updatedGrid[y][x] =
            brandColors[Math.floor(Math.random() * brandColors.length)];
        }
        return updatedGrid;
      });
    }, 600); // Faster updates for more animation

    return () => clearInterval(interval);
  }, []);

  if (grid.length === 0) return null;

  return (
    <div
      className="absolute inset-0 z-0"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
        gridTemplateRows: `repeat(${gridSize}, 1fr)`,
      }}
    >
      {grid.flat().map((color, i) => {
        const row = Math.floor(i / gridSize);
        const col = i % gridSize;
        const stableKey = `${row}-${col}`; // Stable key based on position

        return (
          <motion.div
            key={stableKey}
            animate={{ backgroundColor: hexToRgba(color, 0.3) }}
            transition={{
              duration: 0.6,
              ease: "easeInOut",
            }}
          />
        );
      })}
    </div>
  );
}
